<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      left-side-class="col-xl-6 pb-2"
      :page="headerConfig"
      right-side-class="col-xl-6 align-items-end d-flex pb-4 justify-content-end"
    />
    <document-configuration-generation
      v-if="generationOpen"
      class="mb-3"
      :collections="dataToCreation"
      @on-cancel="
        () => {
          this.generationOpen = false;
        }
      "
      @on-create="createConfiguration($event)"
    />
    <filter-manager
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :display-advanced="false"
      :filters="filtersConfig"
      @area="onAreaChange"
      @search="onSearchHandler(1)"
    />
    <custom-separator class="my-4" text="Listado de Documentos" />
    <custom-result-not-found
      v-if="results.length == 0"
      text="No hay información disponible"
    />
    <result-row v-for="row in results" :id="row.id" :key="row.id" :data="row" />

    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import ResultRow from './components/ResultRow.vue';

import {
  getInitFilters as _getInitFilters,
  getInitDataToCreation as _getInitDataToCreation,
  search as _search,
  create as _create,
} from '@/modules/proposalDocumentConfiguration/api.js';

import proposalRecruitmentTypes from '@/utils/constants/proposalRecruitmentTypes.js';
import proposalAreas from '@/utils/constants/proposalAreas.js';

import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';
import DocumentConfigurationGeneration from './components/DocumentConfigurationGeneration.vue';

const DEFAULT_PAGE_SIZE = 20;

export default {
  name: 'DocumentConfigurationMain',
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
    DocumentConfigurationGeneration,
  },
  mixins: [alertMixin, storeMixin],
  data() {
    return {
      filterOptions: {},
      results: [],
      generationOpen: false,
      dataToCreation: {},
      collections: {
        academicYears: [],
        areas: [],
        providerTypes: [],
      },
      providerTypes: [],
      filterModel: {
        academicYear: null,
        area: null,
        providerType: null,
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    };
  },
  computed: {
    headerConfig() {
      return {
        title: 'Configuración Documentos para Pliegos',
        icon: 'fas fa-file-invoice',
        routes: [
          'Proceso de Competencia',
          'Configuración Documentos para Pliegos',
        ],
      };
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.$router.push({ name: 'infraestructure' });
          },
        },
        {
          text: 'Generar',
          visible: this.canShowAddConfiguration && !this.generationOpen,
          icon: 'fas fa-sync-alt',
          variant: 'success',
          click: () => {
            this.generationOpen = true;
          },
        },
      ];
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          class: 'col-md-4',
          options: this.collections.academicYears,
          initialValue: this.filterModel.academicYear,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
        },
        {
          name: 'area',
          component: 'CustomDropDown',
          placeholder: 'Seleccione área',
          options: this.collections.areas,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-md-4',
          clearable: true,
        },
        {
          name: 'providerType',
          component: 'CustomDropDown',
          placeholder: 'Tipo de Proveedor',
          options: this.collections.providerTypes,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-md-4',
          clearable: true,
        },
      ];
    },
    canShowAddConfiguration() {
      return this.userHasPermissions(
        'provider.Generate.Configuration.Documents'
      );
    },
  },
  async mounted() {
    await this.setInitFilters();
    await this.onSearchHandler(1);
    await this.getDataToCreation();
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async setInitFilters() {
      const { data } = await _getInitFilters();
      if (Object.values(data).length) {
        this.collections.academicYears = data.academicYears;
        this.collections.areas = data.areas;
        this.providerTypes = data.providerTypes;
      }
    },
    async getDataToCreation() {
      const { data } = await _getInitDataToCreation();
      this.dataToCreation = data;
    },
    onAreaChange() {
      this.collections.providerTypes = [];
      this.filterModel.providerType = null;
      if (this.filterModel.area) {
        let providerTypesIds = [
          proposalRecruitmentTypes.Compania,
          proposalRecruitmentTypes.EntidadEducativa,
          proposalRecruitmentTypes.Individuo,
        ];
        if (this.filterModel.area.id == proposalAreas.RemedioProvisional) {
          providerTypesIds = [
            proposalRecruitmentTypes.Compania,
            proposalRecruitmentTypes.DBA,
            proposalRecruitmentTypes.Individuo,
          ];
        }

        this.collections.providerTypes = this.providerTypes.filter((c) =>
          providerTypesIds.includes(c.id)
        );
      }
    },
    async onSearchHandler(page) {
      this.pagination.currentPage = page;

      const { data } = await _search({
        academicYearId: this.filterModel.academicYear?.id,
        areaId: this.filterModel.area?.id,
        recruitmentTypeId: this.filterModel.providerType?.id,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      });

      this.results = data.data;
      this.pagination = {
        ...this.pagination,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    async goToPageHandler(page) {
      await this.onSearchHandler(page);
    },
    async createConfiguration(payload) {
      try {
        await _create(
          payload.academicYearId,
          payload.areaId,
          payload.recruitmentTypeId
        );
        this.ShowToast(
          'Proceso Exitoso',
          'El catálogo ha sido generado exitosamente.',
          'success'
        );
        this.generationOpen = false;
        this.onSearchHandler(1);
      } catch (err) {
        if (err.response.data.message) {
          this.ShowToast('', `${err.response.data.message}`, 'error');
        } else {
          this.ShowToast(
            '',
            'Error al intentar guardar la configuración.',
            'error'
          );
        }
      }
    },
  },
};
</script>
